import React from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle, MessageCircle, CreditCard, Percent } from 'lucide-react';
import { consultingPlans } from './mock';

const Services = () => {
  const handleWhatsAppClick = (planTitle) => {
    const message = `Olá! Tenho interesse no plano ${planTitle} de consultoria para montagem de PC. Podem me dar mais informações?`;
    window.open(`https://wa.me/5512997176654?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section id="services" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-100">
            Nossos Planos
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Escolha o Plano Ideal
            <span className="block text-blue-600">Para Seu Projeto</span>
          </h2>
          <p className="text-xl text-gray-600">
            Oferecemos três planos de consultoria adaptados ao seu orçamento e necessidades. 
            Todos incluem auxílio especializado e serviço de diagnóstico.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {consultingPlans.map((plan, index) => (
            <Card 
              key={plan.id} 
              className={`relative h-full transition-all duration-300 hover:shadow-xl border-2 ${
                index === 1 
                  ? 'border-blue-500 bg-gradient-to-b from-blue-50 to-white scale-105 shadow-lg' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              {index === 1 && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-orange-600 text-white px-4 py-1">
                    Mais Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                  {plan.title}
                </CardTitle>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                
                <div className="space-y-2">
                  <div className="text-4xl font-bold text-gray-900">
                    R$ {plan.price}
                    {plan.priceNote && (
                      <span className="text-sm font-normal text-gray-600 block">
                        {plan.priceNote}
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center justify-center text-green-600 text-sm font-semibold">
                      <CreditCard className="w-4 h-4 mr-1" />
                      {plan.installments}
                    </div>
                    <div className="flex items-center justify-center text-orange-600 text-sm">
                      <Percent className="w-4 h-4 mr-1" />
                      {plan.cashDiscount}
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full py-6 text-lg font-semibold transition-all duration-300 ${
                    index === 1
                      ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                  onClick={() => handleWhatsAppClick(plan.title)}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar {plan.title}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="text-center bg-white rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Todos os Planos Incluem
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Consultoria Completa</h4>
              <p className="text-sm text-gray-600">Desde a escolha até a entrega</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Testes Completos</h4>
              <p className="text-sm text-gray-600">Validação de performance</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Instalação de SO</h4>
              <p className="text-sm text-gray-600">Sistema configurado</p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Suporte Técnico</h4>
              <p className="text-sm text-gray-600">Acompanhamento garantido</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;